import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Stack, Card, CardContent, List, ListItem, ListItemText, Divider
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TaskData } from './TaskData';

interface UserData {
  id: string;
  name: string;
  email: string;
}

interface MarkdownData {
  id: string;
  title: string;
  content: string;
}

const UserTaskDashboard: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserData | null>(null);
  const [taskList, setTaskList] = useState<TaskData[]>([]);
  const [markdownList, setMarkdownList] = useState<MarkdownData[]>([]);

  useEffect(() => {
    const savedUsers = localStorage.getItem('user-list');
    const savedTasks = localStorage.getItem('task-list');
    const savedMarkdowns = localStorage.getItem('markdown-list');
    if (savedUsers && userId) {
      const users: UserData[] = JSON.parse(savedUsers);
      const foundUser = users.find(u => u.id === userId);
      setUser(foundUser || null);
    }
    if (savedTasks) setTaskList(JSON.parse(savedTasks));
    if (savedMarkdowns) setMarkdownList(JSON.parse(savedMarkdowns));
  }, [userId]);

  const isToday = (isoDate?: string) => {
    if (!isoDate) return false;
    const date = new Date(isoDate);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getTaskDurationMinutes = (task: TaskData) => {
    if (task.startedAt && task.finishedAt) {
      const start = new Date(task.startedAt).getTime();
      const end = new Date(task.finishedAt).getTime();
      return Math.floor((end - start) / 60000);
    }
    return 0;
  };

  const stats = (() => {
    const tasks = taskList.filter(t => t.userId === userId);
    const running = tasks.filter(t => t.status === '進行中').length;
    const notStarted = tasks.filter(t => t.status === '未着手').length;
    const finishedToday = tasks.filter(t => t.status === '完了' && isToday(t.finishedAt)).length;
    const totalTimeToday = tasks.reduce((sum, t) => {
      if (t.status === '完了' && isToday(t.finishedAt) && t.startedAt && t.finishedAt) {
        const start = new Date(t.startedAt).getTime();
        const end = new Date(t.finishedAt).getTime();
        return sum + (end - start);
      }
      return sum;
    }, 0);
    const minutes = Math.floor(totalTimeToday / 60000);
    return { running, notStarted, finishedToday, timeMinutes: minutes };
  })();

  const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode, label: string, value: string | number, color: string }) => (
    <Card sx={{ flex: 1, bgcolor: color, color: '#fff', minWidth: 120 }}>
      <CardContent>
        <Stack spacing={1} alignItems="center">
          {icon}
          <Typography variant="h6">{value}</Typography>
          <Typography variant="body2">{label}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  const getMarkdownTitle = (id: string) => {
    const md = markdownList.find(m => m.id === id);
    return md ? md.title : '不明';
  };

  const userTasks = taskList.filter(t => t.userId === userId);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        {user ? `${user.name} さんの本日の状況` : 'ユーザーが見つかりません'}
      </Typography>

      {user && (
        <>
          <Stack direction="row" spacing={2} mt={3} mb={4}>
            <StatCard icon={<PlayArrowIcon />} label="進行中" value={stats.running} color="#1976d2" />
            <StatCard icon={<HourglassEmptyIcon />} label="未着手" value={stats.notStarted} color="#ffa000" />
            <StatCard icon={<CheckCircleIcon />} label="完了" value={stats.finishedToday} color="#2e7d32" />
            <StatCard icon={<AccessTimeIcon />} label="作業時間(分)" value={stats.timeMinutes} color="#6a1b9a" />
          </Stack>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>タスク一覧</Typography>
            <List>
              {userTasks.map((task) => (
                <React.Fragment key={task.id}>
                  <ListItem>
                    <ListItemText
                      primary={getMarkdownTitle(task.markdownId)}
                      secondary={`ステータス: ${task.status} / 作成日: ${new Date(task.createdAt).toLocaleString()} / 作業時間: ${getTaskDurationMinutes(task)}分`}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {userTasks.length === 0 && (
                <ListItem>
                  <ListItemText primary="タスクがありません。" />
                </ListItem>
              )}
            </List>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default UserTaskDashboard;